import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { IdentificationCard, Plus, Key, Prohibit, CheckCircle, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errorText = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "never";

// Oct 26 2026, user's ask: "Allow admin to create user /password for the
// operator and supervisor" - people with no company mailbox, who reach the
// Consumption Register and nothing else.
export const FloorLoginsTab = () => {
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", name: "", password: "" });
  const [busy, setBusy] = useState("");
  const [resetting, setResetting] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  // Switching a login off drops his live sessions, so it asks once first.
  const [confirmOff, setConfirmOff] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/floor-logins`);
      setLogins(data.logins || []);
    } catch (e) {
      toast.error(errorText(e, "Could not load the floor logins"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy("create");
    try {
      const { data } = await axios.post(`${API}/admin/floor-logins`, form);
      toast.success(`${data.username} can sign in now`, {
        description: "Hand over the password - it works once, then he sets his own.",
      });
      setForm({ username: "", name: "", password: "" });
      load();
    } catch (e) {
      toast.error(errorText(e, "Could not create that login"));
    } finally {
      setBusy("");
    }
  };

  const reset = async (row) => {
    setBusy(row.id);
    try {
      await axios.post(`${API}/admin/floor-logins/${row.id}/reset-password`, { password: resetPassword });
      toast.success(`${row.username}'s password is reset`, {
        description: "He will be asked to set his own at the next sign-in.",
      });
      setResetting(null);
      setResetPassword("");
      load();
    } catch (e) {
      toast.error(errorText(e, "Could not reset that password"));
    } finally {
      setBusy("");
    }
  };

  const toggleActive = async (row) => {
    setBusy(row.id);
    try {
      await axios.post(`${API}/admin/floor-logins/${row.id}/active`, { active: !row.active });
      toast.success(row.active ? `${row.username} is switched off` : `${row.username} can sign in again`);
      setConfirmOff(null);
      load();
    } catch (e) {
      toast.error(errorText(e, "Could not change that login"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="floor-logins-tab">
      <p className="text-sm text-[#667085]">
        Operators and supervisors with no company mailbox sign in with a username here. A floor login reaches the
        Consumption Register and nothing else, and the password you type works for one sign-in only - he sets his own
        straight after.
      </p>

      <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 space-y-2" data-testid="floor-login-create">
        <div className="flex items-center gap-2">
          <IdentificationCard size={16} weight="fill" className="text-[#0E7C86]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#344054] font-heading">New floor login</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Input
            className="h-10" placeholder="Username e.g. hdg.operator1"
            autoCapitalize="none" autoCorrect="off"
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase() }))}
            data-testid="floor-login-new-username"
          />
          <Input
            className="h-10" placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            data-testid="floor-login-new-name"
          />
          <Input
            className="h-10" placeholder="First password (6+ characters)"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            data-testid="floor-login-new-password"
          />
        </div>
        <Button
          className="h-10 bg-[#0E7C86] hover:bg-[#0B6B74]"
          disabled={busy === "create" || !form.username || form.password.length < 6}
          onClick={create}
          data-testid="floor-login-create-submit"
        >
          {busy === "create" ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : <Plus size={14} weight="bold" className="mr-1.5" />}
          Create login
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-[#667085]" data-testid="floor-logins-loading">Loading...</p>
      ) : logins.length === 0 ? (
        <p className="text-sm text-[#667085]" data-testid="floor-logins-empty">No floor logins yet.</p>
      ) : (
        <div className="space-y-2">
          {logins.map((row) => (
            <div
              key={row.id}
              className="bg-white border border-[#D0D5DD] rounded-sm p-3 space-y-2"
              data-testid={`floor-login-${row.username}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-[#101828]">{row.username}</span>
                    {row.active ? (
                      <Badge className="bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]">Active</Badge>
                    ) : (
                      <Badge className="bg-[#FEF3F2] text-[#B42318] border-[#FDA29B]">Switched off</Badge>
                    )}
                    {row.must_change_password && (
                      <Badge className="bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]">Must set own password</Badge>
                    )}
                  </div>
                  <div className="text-xs text-[#667085]">
                    {row.name} · Consumption Register only · last signed in {when(row.last_login_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm" variant="outline" disabled={busy === row.id}
                    onClick={() => { setResetting(resetting === row.id ? null : row.id); setResetPassword(""); }}
                    data-testid={`floor-login-reset-${row.username}`}
                  >
                    <Key size={14} weight="bold" className="mr-1.5" /> Reset password
                  </Button>
                  <Button
                    size="sm" variant="outline" disabled={busy === row.id}
                    onClick={() => (row.active && confirmOff !== row.id ? setConfirmOff(row.id) : toggleActive(row))}
                    className={row.active && confirmOff === row.id ? "border-[#B42318] text-[#B42318]" : ""}
                    data-testid={`floor-login-toggle-${row.username}`}
                  >
                    {!row.active
                      ? <><CheckCircle size={14} weight="bold" className="mr-1.5" /> Switch on</>
                      : confirmOff === row.id
                        ? <><Prohibit size={14} weight="bold" className="mr-1.5" /> Confirm - signs him out</>
                        : <><Prohibit size={14} weight="bold" className="mr-1.5" /> Switch off</>}
                  </Button>
                </div>
              </div>

              {resetting === row.id && (
                <div className="flex items-center gap-2 bg-[#F9FAFB] border border-[#EAECF0] rounded-sm p-2" data-testid={`floor-login-reset-form-${row.username}`}>
                  <Input
                    className="h-9" placeholder="Temporary password (6+ characters)"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    data-testid={`floor-login-reset-input-${row.username}`}
                  />
                  <Button
                    size="sm" className="bg-[#0E7C86] hover:bg-[#0B6B74] shrink-0"
                    disabled={busy === row.id || resetPassword.length < 6}
                    onClick={() => reset(row)}
                    data-testid={`floor-login-reset-submit-${row.username}`}
                  >
                    Set it
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
